import { useEffect } from 'react';
import { AccountApiProvider } from '../api/AccountApiProvider';
import { AccountSessionProvider, useAccountSession } from '../auth/AccountSessionProvider';
import { allowedRoutes, homeFor, isTenancyRepresentative } from '../auth/accountRoutes';
import { isAccountRole } from '../api/types';
import type { AccountRole } from '../api/types';
import { useI18n } from '../i18n/useI18n';
import type { SimpleKey } from '../i18n/types';
import { useRouter } from '../router/Router';
import { asAccountRoute } from '../router/routes';
import { AccountLoginScreen } from '../screens/account/AccountLoginScreen';
import { AccountNoAccessScreen } from '../screens/account/AccountNoAccessScreen';
import { IdentityProviderScreen } from '../screens/account/IdentityProviderScreen';
import { PasswordScreen } from '../screens/account/PasswordScreen';
import { RolloutScreen } from '../screens/account/RolloutScreen';
import { TenancyScreen } from '../screens/account/TenancyScreen';
import { TenantScreen } from '../screens/account/TenantScreen';
import { TenantsScreen } from '../screens/account/TenantsScreen';
import { UsageScreen } from '../screens/account/UsageScreen';
import { Card, EmptyState, Loading } from '../ui/primitives';
import { IconCatalog, IconIdentity, IconKey, IconTenants, IconVersions } from '../ui/icons';
import { AppShell } from './AppShell';
import type { NavEntry, ShellNav } from './AppShell';

/**
 * The account, on the provider's own domain.
 *
 * Subscription work, tenancies and the choice of identity provider live here,
 * and the way in is a provider account. A tenancy's hosts and databases do not
 * live here and cannot be reached from here — that is the whole reason the
 * console is split in two.
 */
export function AccountApp() {
  return (
    <AccountApiProvider>
      <AccountSessionProvider>
        <AccountRoutes />
      </AccountSessionProvider>
    </AccountApiProvider>
  );
}

const ROLE_LABEL: Record<AccountRole, SimpleKey> = {
  provider_admin: 'shell.role.providerAdmin',
  provider_support: 'shell.role.providerSupport',
  tenant_admin: 'shell.role.tenantAdmin',
};

function AccountRoutes() {
  const { t } = useI18n();
  const { status, session, signOut } = useAccountSession();
  const { route, navigate } = useRouter();

  const accountRoute = asAccountRoute(route);

  // Keep the address honest: land on a part of the account this session has.
  useEffect(() => {
    if (session === null || !isAccountRole(session.role)) return;
    if (accountRoute === null) return;
    if (!allowedRoutes(session).includes(accountRoute.name)) {
      navigate(homeFor(session), { replace: true });
    }
  }, [session, accountRoute, navigate]);

  if (status === 'loading') return <Loading />;
  if (session === null) return <AccountLoginScreen />;
  if (!isAccountRole(session.role)) return <AccountNoAccessScreen />;

  // The menu is the allowed addresses, drawn. Every entry is filtered through
  // the same list the address bar is checked against, so a menu entry that led
  // somewhere this session is bounced off cannot exist.
  const open = allowedRoutes(session);
  const entries: NavEntry[] = isTenancyRepresentative(session)
    ? [
        {
          route: { name: 'account.tenancy' },
          label: t('nav.account.tenancy'),
          icon: <IconTenants />,
          matches: ['account.tenancy'],
        },
        {
          route: { name: 'account.tenancyUsage' },
          label: t('nav.account.usage'),
          icon: <IconCatalog />,
          matches: ['account.tenancyUsage'],
        },
        {
          route: { name: 'account.identityProvider' },
          label: t('nav.account.identityProvider'),
          icon: <IconIdentity />,
          matches: ['account.identityProvider'],
        },
      ]
    : [
        {
          route: { name: 'account.tenants' },
          label: t('nav.account.tenants'),
          icon: <IconTenants />,
          matches: ['account.tenants', 'account.tenant', 'account.usage'],
        },
        {
          route: { name: 'account.rollout' },
          label: t('nav.account.rollout'),
          icon: <IconVersions />,
          matches: ['account.rollout'],
        },
      ];

  const nav: ShellNav[] = [
    {
      group: t(isTenancyRepresentative(session) ? 'nav.group.tenancy' : 'nav.group.fleet'),
      items: entries.filter((entry) => open.includes(entry.route.name)),
    },
    {
      group: t('nav.group.account'),
      items: [
        {
          route: { name: 'account.password' },
          label: t('nav.account.password'),
          icon: <IconKey />,
          matches: ['account.password'],
        },
      ],
    },
  ];

  const tenancy = session.tenancy;
  const shell = {
    scope: {
      name: tenancy?.display_name ?? t('shell.scope.provider'),
      sub: tenancy?.slug ?? t('shell.scope.provider.sub'),
    },
    actor: { name: session.actor.display_name, role: t(ROLE_LABEL[session.role]) },
    nav,
    onSignOut: () => {
      void signOut();
    },
  };

  if (accountRoute === null) {
    return (
      <AppShell {...shell} crumb={t('route.notFound.title')}>
        <NotFound />
      </AppShell>
    );
  }

  switch (accountRoute.name) {
    case 'account.tenant':
      return (
        <AppShell {...shell} crumb={t('tenants.title')}>
          <TenantScreen slug={accountRoute.slug} />
        </AppShell>
      );
    case 'account.usage':
      return (
        <AppShell {...shell} crumb={t('usage.title')}>
          <UsageScreen slug={accountRoute.slug} back={{ name: 'account.tenant', slug: accountRoute.slug }} />
        </AppShell>
      );
    case 'account.tenancy':
      return (
        <AppShell {...shell} crumb={t('tenancy.title')}>
          <TenancyScreen />
        </AppShell>
      );
    // The slug is the session's, never the address's: which tenancy a
    // representative acts for is not a thing anybody types.
    case 'account.tenancyUsage':
      return (
        <AppShell {...shell} crumb={t('usage.title')}>
          {tenancy === undefined ? (
            <NotSoleTenancy />
          ) : (
            <UsageScreen slug={tenancy.slug} />
          )}
        </AppShell>
      );
    case 'account.rollout':
      return (
        <AppShell {...shell} crumb={t('rollout.title')}>
          <RolloutScreen />
        </AppShell>
      );
    case 'account.identityProvider':
      return (
        <AppShell {...shell} crumb={t('identityProvider.title')}>
          <IdentityProviderScreen />
        </AppShell>
      );
    case 'account.password':
      return (
        <AppShell {...shell} crumb={t('password.title')}>
          <PasswordScreen />
        </AppShell>
      );
    case 'account.login':
    case 'account.tenants':
      return (
        <AppShell {...shell} crumb={t('tenants.title')}>
          <TenantsScreen />
        </AppShell>
      );
  }
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="content-inner">
      <Card>
        <EmptyState title={t('route.notFound.title')} body={t('route.notFound.body')} />
      </Card>
    </div>
  );
}

/**
 * An account that carries the capability of a representative and is bound to no
 * one tenancy.
 *
 * The server sets `tenancy` only where there is exactly one, so nothing here
 * can pick one — and picking one would be this console deciding whose figures
 * somebody reads.
 */
function NotSoleTenancy() {
  const { t } = useI18n();
  return (
    <div className="content-inner">
      <Card>
        <EmptyState
          title={t('identityProvider.notSole.title')}
          body={t('identityProvider.notSole.body')}
        />
      </Card>
    </div>
  );
}
